import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Check, X, MapPin, ArrowRight, AlertCircle } from 'lucide-react';
import api from '../../services/api';

const BulkSeatUpdateModal = ({ open, onOpenChange, project, onUpdated }) => {
  const [rawText, setRawText] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(false);

  const parseRows = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    return lines.map(line => {
      // Try tab-separated first (5+ parts)
      const tabParts = line.split('\t');
      if (tabParts.length >= 5) {
        return {
          access: tabParts[0]?.trim() || '',
          stairs: tabParts[1]?.trim() || '',
          row: tabParts[2]?.trim() || '',
          seat: tabParts[3]?.trim() || '',
          reference: tabParts[4]?.trim() || ''
        };
      }
      // Fallback: split by any whitespace
      const parts = line.trim().split(/\s+/);
      if (parts.length >= 5) {
        // Last part is reference, first 4 are access/stairs/row/seat
        const reference = parts[parts.length - 1];
        return {
          access: parts[0] || '',
          stairs: parts[1] || '',
          row: parts[2] || '',
          seat: parts[3] || '',
          reference: reference
        };
      }
      if (parts.length === 1) {
        // Just a reference on its own line
        return { access: '', stairs: '', row: '', seat: '', reference: parts[0] };
      }
      // Less than 5 parts but more than 1 - last is reference
      const reference = parts[parts.length - 1];
      return { access: '', stairs: '', row: '', seat: '', reference: reference };
    }).filter(r => {
      const ref = r.reference.toLowerCase();
      if (ref === 'reference' || ref === 'ref') return false;
      return r.reference.length > 0;
    });
  };

  const handlePreview = async () => {
    const rows = parseRows(rawText);
    if (rows.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post('/tickets/bulk-update-seats', {
        rows,
        project,
        mode: 'preview'
      });
      setPreview(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    const rows = parseRows(rawText);
    if (rows.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post('/tickets/bulk-update-seats', {
        rows,
        project,
        mode: 'apply'
      });
      setPreview(res.data);
      setApplied(true);
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRawText('');
    setPreview(null);
    setApplied(false);
    onOpenChange(false);
  };

  const hasData = (v) => v && v.trim() && v.trim().toUpperCase() !== 'X';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#C4A052]" />
            MAJ Emplacements en masse
          </DialogTitle>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-zinc-400">
              Collez les données depuis votre tableau. Format attendu (séparé par tabulations) :
            </p>
            <div className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-xs font-mono text-zinc-400">
              Access &nbsp; Stairs &nbsp; Row &nbsp; Seats &nbsp; Reference
            </div>
            <textarea
              data-testid="bulk-seat-textarea"
              className="w-full h-64 bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-sm font-mono text-zinc-200 focus:border-[#C4A052] focus:outline-none resize-none"
              placeholder={`C32\t19\t1\t76\tKOLAR-06\nC32\t19\t1\t77\tKOLAR-06\n\t\t\t\tKOLAR-05\nX\tX\tX\tX\tKOLAR-01`}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">
                {parseRows(rawText).length} ligne(s) detectee(s)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleClose} className="border-zinc-600">
                  Annuler
                </Button>
                <Button
                  onClick={handlePreview}
                  disabled={loading || parseRows(rawText).length === 0}
                  className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                  data-testid="bulk-seat-preview-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                  Apercu
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            {/* Stats bar */}
            <div className="flex gap-4 text-sm">
              <span className="px-3 py-1 rounded bg-emerald-500/15 text-emerald-400 font-medium">
                {preview.total_updated} MAJ
              </span>
              <span className="px-3 py-1 rounded bg-zinc-700/50 text-zinc-400">
                {preview.total_skipped} sans donnees
              </span>
              {preview.total_not_found > 0 && (
                <span className="px-3 py-1 rounded bg-red-500/15 text-red-400">
                  {preview.total_not_found} non trouves
                </span>
              )}
            </div>

            {/* Results table */}
            <div className="overflow-y-auto max-h-[55vh] border border-zinc-800 rounded-lg">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-zinc-900 z-10">
                  <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                    <th className="px-2 py-2 text-left">Ref</th>
                    <th className="px-2 py-2 text-left">Statut</th>
                    <th className="px-2 py-2 text-left">Acces</th>
                    <th className="px-2 py-2 text-left">Escalier</th>
                    <th className="px-2 py-2 text-left">Rang</th>
                    <th className="px-2 py-2 text-left">Place</th>
                    <th className="px-2 py-2 text-left">Actuel</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.results.map((r, i) => {
                    const isUpdate = r.status === 'update';
                    const isSkip = r.status === 'skip';
                    const isNotFound = r.status === 'not_found' || r.status === 'extra_row';
                    return (
                      <tr
                        key={i}
                        className={`border-t border-zinc-800/50 ${
                          isUpdate ? 'bg-emerald-500/5' : isNotFound ? 'bg-red-500/5' : 'bg-zinc-900/30'
                        }`}
                      >
                        <td className="px-2 py-1.5 font-mono text-xs text-zinc-300">{r.reference}</td>
                        <td className="px-2 py-1.5">
                          {isUpdate && (
                            <span className="flex items-center gap-1 text-emerald-400 text-xs">
                              <Check className="h-3 w-3" /> MAJ
                            </span>
                          )}
                          {isSkip && (
                            <span className="text-zinc-500 text-xs">-</span>
                          )}
                          {isNotFound && (
                            <span className="flex items-center gap-1 text-red-400 text-xs">
                              <AlertCircle className="h-3 w-3" /> Introuvable
                            </span>
                          )}
                        </td>
                        <td className={`px-2 py-1.5 text-xs font-mono ${hasData(r.input.access) ? 'text-emerald-300' : 'text-zinc-600'}`}>
                          {r.input.access || '-'}
                        </td>
                        <td className={`px-2 py-1.5 text-xs font-mono ${hasData(r.input.stairs) ? 'text-emerald-300' : 'text-zinc-600'}`}>
                          {r.input.stairs || '-'}
                        </td>
                        <td className={`px-2 py-1.5 text-xs font-mono ${hasData(r.input.row) ? 'text-emerald-300' : 'text-zinc-600'}`}>
                          {r.input.row || '-'}
                        </td>
                        <td className={`px-2 py-1.5 text-xs font-mono ${hasData(r.input.seat) ? 'text-emerald-300' : 'text-zinc-600'}`}>
                          {r.input.seat || '-'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-zinc-500">
                          {r.current ? (
                            `${r.current.acces || '-'} / ${r.current.escalier || '-'} / ${r.current.rang || '-'} / ${r.current.place || '-'}`
                          ) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              {applied ? (
                <Button onClick={handleClose} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                  <Check className="h-4 w-4 mr-2" />
                  Fermer
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setPreview(null)} className="border-zinc-600">
                    Retour
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={loading || preview.total_updated === 0}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    data-testid="bulk-seat-apply-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    Appliquer ({preview.total_updated} MAJ)
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkSeatUpdateModal;
